import { useState, useEffect, useMemo } from "react";
import { IoChevronDown } from "react-icons/io5";
import { 
  FaCalendarAlt, 
  FaClock, 
  FaMapMarkerAlt, 
  FaDownload, 
  FaCheckCircle, 
  FaBookOpen,
  FaArrowLeft,
  FaGraduationCap
} from "react-icons/fa";
import "./CohortDetails.css";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { fetchCourses } from "../../redux/slices/frontend/courseSlice";
import { fetchUserEnrolledCohorts } from "../../redux/slices/userEnrolledCohortSlice";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "react-toastify";

const CohortDetails = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { courses, loading, error } = useSelector((state) => state.courses);
  const { enrollment: userEnrollments } = useSelector((state) => state.userEnrolledCohorts);
  const { slug } = useParams();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchCourses());
    if (localStorage.getItem('authToken')) {
      dispatch(fetchUserEnrolledCohorts());
    }
  }, [dispatch]);
 
  // Slug filter
  const filteredCourse = useMemo(() => {
    const result = courses.filter((course) => {
      const matchesSearch =
        course?.slug === slug ||
        (course?.cohorts || []).some((cohort) => cohort?.slug === slug);
      return matchesSearch;
    });
    return result[0];
  }, [courses, slug]);

  // Find the active cohort matching the slug, or default to the first one
  const activeCohort = useMemo(() => {
    if (!filteredCourse?.cohorts) return null;
    return filteredCourse.cohorts.find((c) => c?.slug === slug) || filteredCourse.cohorts[0];
  }, [filteredCourse, slug]); 

  const userEnrollment = useMemo(
    () => userEnrollments?.find((e) => e.cohort?.slug === slug) ?? null,
    [userEnrollments, slug],
  );
  const isPrivateClass = userEnrollment?.class_option === "Private Class";
  const isPrivateStudent = isAuthenticated && (
    user?.role === 'private_student' || 
    userEnrollments?.some(e => e.class_option === 'Private Class')
  );
  const [expandedSections, setExpandedSections] = useState({});
  const [currency, setCurrency] = useState("USD");
  // Exchange rate state
  const [usdToNgn, setUsdToNgn] = useState(null);
  const [rateLoading, setRateLoading] = useState(true);

  useEffect(() => {
    // Fetch exchange rate from provided Exchange Rate API
    const fetchRate = async () => {
      setRateLoading(true);
      try {
        const res = await fetch("https://v6.exchangerate-api.com/v6/689d396c0a109d335cc4092c/latest/USD");
        const data = await res.json();
        setUsdToNgn(data.conversion_rates?.NGN);
      } catch (e) {
        setUsdToNgn(null);
      } finally {
        setRateLoading(false);
      }
    };
    fetchRate();
  }, []);

  useEffect(() => {
    if (filteredCourse?.curriculum) {
      setExpandedSections(
        Object.fromEntries(
          (filteredCourse.curriculum || []).map((item) => [item.title, false]),
        ),
      );
    }
  }, [filteredCourse]);

  const stripHtml = (html) => {
    if (!html) return "";
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };
  const handleRegisterCourse = (cohortId) => {
    setTimeout(() => {
      navigate("/cohort/register", {
        state: { 
          cohort_id: cohortId || null, 
          course_id: filteredCourse?.id || null,
          cohortName: filteredCourse?.title,
          coursePrice: filteredCourse?.price,
          cohortPrice: activeCohort?.dollar_price || null,
          isPrivateClass: filteredCourse?.type === "private"
        },
      });
    }, 250);
  };

  const handleDownloadBrochure = () => {
    const brochureUrl = activeCohort?.brochure_path;
    if (brochureUrl) {
      window.open(brochureUrl);
    } else {
      toast.info("Brochure not available.")
    }
  }
  return (
    <div className="details-container">
      {/* Breadcrumb / Back Navigation */}
      <div className="back-navigation">
        <button className="btn-back" onClick={() => navigate(-1)}>
          <FaArrowLeft className="back-icon" />
          <span>Back to Cohorts</span>
        </button>
      </div>

      {/* Hero / Header Card */}
      <div className="details-hero">
        <span className="hero-badge">{filteredCourse?.category || "Training Program"}</span>
        <h1>{filteredCourse?.title || ""}</h1>
        <p className="hero-subtitle">
          {filteredCourse?.description ? stripHtml(filteredCourse.description).substring(0, 180) + "..." : ""}
        </p>
      </div>

      {/* Main 2-Column Grid */}
      <div className="details-grid-layout">
        {/* Left Column: Course details & curriculum */}
        <div className="details-main-content">
          {/* Overview Section */}
          <section className="info-card overview-section">
            <h3>About this Course</h3>
            {loading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading Description...</p>
              </div>
            ) : (
              <p className="description-text">{stripHtml(filteredCourse?.description) || ""}</p>
            )}
          </section>

          {/* Prerequisites Section */}
          <section className="info-card prerequisites-section">
            <h3>
              <FaGraduationCap className="section-title-icon" />
              Prerequisites
            </h3>
            {loading && (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading Prerequisites...</p>
              </div>
            )}
            <ul className="prereq-list">
              {!loading && !error && (
                <>
                  {filteredCourse?.prerequisite?.length > 0 ? (
                    (filteredCourse.prerequisite || []).map((prerequisite, index) => (
                      <li key={index} className="prereq-item">
                        <FaCheckCircle className="check-icon" />
                        <span>{prerequisite.title}</span>
                      </li>
                    ))
                  ) : (
                    <li className="prereq-item no-prereq">
                      <span>No prerequisites required. This course is beginner-friendly!</span>
                    </li>
                  )}
                </>
              )}
            </ul>
          </section>

          {/* Curriculum Section */}
          <section className="info-card curriculum-section">
            <h3>
              <FaBookOpen className="section-title-icon" />
              Course Curriculum
            </h3>
            {loading && (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading Curriculum...</p>
              </div>
            )}
            {!loading && !error && (
              <div className="curriculum-list">
                {(filteredCourse?.curriculum || []).map((item, index) => (
                  <div className={`curriculum-item-card ${expandedSections[item.title] ? "active" : ""}`} key={index}>
                    <div
                      className="curriculum-header-trigger"
                      onClick={() => toggleSection(item.title)}
                    >
                      <span className="curriculum-item-title">{item.title}</span>
                      <IoChevronDown
                        className={`chevron-icon ${expandedSections[item.title] ? "rotated" : ""}`}
                      />
                    </div>
                    {expandedSections[item.title] && (
                      <div className="curriculum-body-content">
                        <p>{item.description}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* Right Column: Sticky Pricing & Enrollment Card */}
        <div className="details-sidebar">
          <div className="sidebar-sticky-card">
            <div className="sidebar-header-badge">
              {filteredCourse?.type === "private" ? "Private Class Only" : "Cohort Registration"}
            </div>
            
            {/* Pricing Section */}
            <div className="sidebar-pricing-box">
              <div className="pricing-header-row">
                <span className="price-label">Investment</span>
                <div className="currency-pill-toggle">
                  <button
                    className={`currency-btn-pill ${currency === "NGN" ? "active" : ""}`}
                    onClick={() => setCurrency("NGN")}
                    type="button"
                  >
                    ₦ NGN
                  </button>
                  <button
                    className={`currency-btn-pill ${currency === "USD" ? "active" : ""}`}
                    onClick={() => setCurrency("USD")}
                    type="button"
                  >
                    $ USD
                  </button>
                </div>
              </div>
              
              <div className="price-display-wrapper">
                {currency === "USD" ? (
                  <div className="price-amount">
                    <span className="currency-symbol">$</span>
                    <span className="amount-val">
                      {activeCohort?.dollar_price
                        ? parseFloat(activeCohort.dollar_price).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
                        : "N/A"}
                    </span>
                  </div>
                ) : (
                  <div className="price-amount">
                    <span className="currency-symbol">₦</span>
                    <span className="amount-val">
                      {rateLoading ? (
                        <span className="loading-text">...</span>
                      ) : usdToNgn && activeCohort?.dollar_price ? (
                        (parseFloat(activeCohort.dollar_price) * usdToNgn).toLocaleString(undefined, { maximumFractionDigits: 0 })
                      ) : (
                        "N/A"
                      )}
                    </span>
                  </div>
                )}
                <span className="price-subtext">All-inclusive cohort fee (Weekday & Weekend)</span>
              </div>
            </div>

            {/* Course Meta Info List */}
            <div className="sidebar-meta-list">
              {filteredCourse?.location && (
                <div className="meta-list-item">
                  <div className="meta-item-icon-box">
                    <FaMapMarkerAlt />
                  </div>
                  <div className="meta-item-text">
                    <span className="meta-item-label">Location</span>
                    <span className="meta-item-val">{filteredCourse.location}</span>
                  </div>
                </div>
              )}

              {isPrivateStudent ? (
                <div className="meta-list-item">
                  <div className="meta-item-icon-box">
                    <FaClock />
                  </div>
                  <div className="meta-item-text">
                    <span className="meta-item-label">Private Class Duration</span>
                    <span className="meta-item-val">Flexible / Custom (e.g. 3 Months)</span>
                  </div>
                </div>
              ) : isPrivateClass ? (
                <div className="meta-list-item">
                  <div className="meta-item-icon-box">
                    <FaClock />
                  </div>
                  <div className="meta-item-text">
                    <span className="meta-item-label">Your Duration</span>
                    <span className="meta-item-val">{userEnrollment.duration ?? "TBD"}</span>
                  </div>
                </div>
              ) : (
                <>
                  {activeCohort?.start_date && (
                    <div className="meta-list-item">
                      <div className="meta-item-icon-box">
                        <FaCalendarAlt />
                      </div>
                      <div className="meta-item-text">
                        <span className="meta-item-label">Starting Date</span>
                        <span className="meta-item-val">{formatDate(activeCohort.start_date)}</span>
                      </div>
                    </div>
                  )}

                  {activeCohort?.end_date && (
                    <div className="meta-list-item">
                      <div className="meta-item-icon-box">
                        <FaCalendarAlt />
                      </div>
                      <div className="meta-item-text">
                        <span className="meta-item-label">Ending Date</span>
                        <span className="meta-item-val">{formatDate(activeCohort.end_date)}</span>
                      </div>
                    </div>
                  )}

                  {activeCohort?.duration && (
                    <div className="meta-list-item">
                      <div className="meta-item-icon-box">
                        <FaClock />
                      </div>
                      <div className="meta-item-text">
                        <span className="meta-item-label">Duration</span>
                        <span className="meta-item-val">{activeCohort.duration}</span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Primary Call to Action buttons */}
            <div className="sidebar-actions">
              <button
                className="btn-enroll-now"
                onClick={() => handleRegisterCourse(activeCohort?.id)}
              >
                Enroll in Cohort
              </button>
              <button 
                className="btn-download-brochure-sidebar" 
                onClick={handleDownloadBrochure}
              >
                <FaDownload className="download-btn-icon" />
                Download Syllabus
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CohortDetails;
